/**
 * Utilidades para expansión de períodos y detección de solapamientos.
 * Replica la lógica del backend para recalcular en el frontend al editar registros.
 */

// ══════════════════════════════════════
// NORMALIZACIÓN DE FORMATO
// ══════════════════════════════════════

/**
 * Normaliza cualquier formato de yearMonth a "YYYY-MM" canónico.
 * Acepta: "YYYY-MM", "MM/YYYY", "YYYY-M", "M/YYYY", etc.
 * Siempre retorna con mes de 2 dígitos y año de 4 dígitos.
 */
export const normalizarYearMonth = (input) => {
    if (!input) return null;
    const trimmed = String(input).trim();
    if (!trimmed) return null;

    let year, month;

    if (trimmed.includes('-')) {
        const parts = trimmed.split('-');
        if (parts.length === 2) {
            year = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10);
        }
    } else if (trimmed.includes('/')) {
        const parts = trimmed.split('/');
        if (parts.length === 2) {
            month = parseInt(parts[0], 10);
            year = parseInt(parts[1], 10);
        }
    }

    if (year && month && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        return `${year}-${String(month).padStart(2, '0')}`;
    }
    return null;
};

// ══════════════════════════════════════
// FECHA DE NACIMIENTO / REGLA 18 AÑOS
// ══════════════════════════════════════

/**
 * Calcula el primer mes computable para una persona: el mes en que cumple 18 años.
 * Meses anteriores a este no se computan para la jubilación.
 * @param {string} fechaNacimientoIso - "YYYY-MM-DD" o "YYYY-MM"
 * @returns {string|null} "YYYY-MM"
 */
export const calcularPrimerMesComputable = (fechaNacimientoIso) => {
    if (!fechaNacimientoIso) return null;
    const m = /^(\d{4})-(\d{2})/.exec(String(fechaNacimientoIso));
    if (!m) return null;
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
    return `${year + 18}-${String(month).padStart(2, '0')}`;
};

// ══════════════════════════════════════
// RESUMEN HISTORIA LABORAL (existente)
// ══════════════════════════════════════

/**
 * Parsea un período "MM/YYYY" a un objeto { year, month }.
 */
export const parsearPeriodo = (periodo) => {
    const [mm, yyyy] = periodo.split('/');
    return { year: parseInt(yyyy, 10), month: parseInt(mm, 10) };
};

/**
 * Formatea un yearMonth "YYYY-MM" a "MM/YYYY" para display.
 */
export const formatearYearMonth = (yearMonth) => {
    const [yyyy, mm] = yearMonth.split('-');
    return `${mm}/${yyyy}`;
};

/**
 * Expande un rango de períodos (inclusivo) en meses individuales.
 * @param {string} desde - "MM/YYYY"
 * @param {string} hasta - "MM/YYYY"
 * @returns {string[]} Lista de "YYYY-MM"
 */
export const expandirRango = (desde, hasta) => {
    const meses = [];
    const inicio = parsearPeriodo(desde);
    const fin = parsearPeriodo(hasta);
    let year = inicio.year;
    let month = inicio.month;
    const finTotal = fin.year * 12 + fin.month;

    while (year * 12 + month <= finTotal) {
        meses.push(`${year}-${String(month).padStart(2, '0')}`);
        month++;
        if (month > 12) { month = 1; year++; }
    }
    return meses;
};

/**
 * Expande todos los registros del Resumen y detecta solapamientos.
 */
export const expandirYAnalizar = (registros) => {
    const mesAOrigenes = new Map();
    let totalMesesDeclarados = 0;

    for (const registro of registros) {
        if (!registro.desde || !registro.hasta) continue;
        try {
            const meses = expandirRango(registro.desde, registro.hasta);
            totalMesesDeclarados += meses.length;
            for (const mes of meses) {
                if (!mesAOrigenes.has(mes)) mesAOrigenes.set(mes, []);
                mesAOrigenes.get(mes).push(registro.indiceOriginal);
            }
        } catch (e) {
            console.warn(`Error expandiendo registro ${registro.indiceOriginal}:`, e);
        }
    }

    const sortedEntries = Array.from(mesAOrigenes.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    const mesesExpandidos = sortedEntries.map(([yearMonth, origenes]) => ({
        yearMonth,
        origenIndices: origenes,
        superpuesto: origenes.length > 1,
    }));

    const solapamientos = mesesExpandidos
        .filter((m) => m.superpuesto)
        .map((m) => {
            const empleadores = m.origenIndices.map((idx) => {
                const reg = registros.find((r) => r.indiceOriginal === idx);
                return reg ? `${reg.razonSocial} (${reg.cuitCuenta})` : 'Desconocido';
            });
            return {
                yearMonth: m.yearMonth,
                registrosInvolucrados: m.origenIndices,
                empleadoresInvolucrados: empleadores,
            };
        });

    return {
        mesesExpandidos,
        solapamientos,
        totalMesesDeclarados,
        totalMesesUnicos: mesesExpandidos.length,
        totalMesesSuperpuestos: solapamientos.length,
    };
};

/**
 * Valida que un período tenga formato válido MM/YYYY.
 */
export const validarPeriodo = (periodo) => {
    if (!periodo) return false;
    return /^(0[1-9]|1[0-2])\/\d{4}$/.test(periodo);
};

/**
 * Valida que desde <= hasta.
 */
export const validarRango = (desde, hasta) => {
    if (!validarPeriodo(desde) || !validarPeriodo(hasta)) return false;
    const d = parsearPeriodo(desde);
    const h = parsearPeriodo(hasta);
    return d.year * 12 + d.month <= h.year * 12 + h.month;
};

// ══════════════════════════════════════
// HASTA 06/94
// ══════════════════════════════════════

/**
 * Calcula el total de meses trabajados de los registros HASTA 06/94.
 */
export const calcularTotalMesesHasta0694 = (registros) => {
    return analizarRegistrosHasta0694(registros).totalMesesUnicos;
};

const normalizarAnioHasta0694 = (anioRaw) => {
    const n = Number.parseInt(String(anioRaw ?? '').trim(), 10);
    if (!Number.isFinite(n)) return null;
    if (n >= 1900 && n <= 2100) return n;
    if (n >= 0 && n <= 99) return 1900 + n;
    return null;
};

const parsearAltaBajaHasta0694 = (altaBajaRaw) => {
    const value = String(altaBajaRaw || '').trim();
    if (!value) return null;

    const normalizado = value
        .replace(/[–—−]/g, '-')
        .replace(/[.,]/g, '/')
        .replace(/\s+/g, ' ')
        .trim();

    const match = normalizado.match(/(\d{1,2})\s*[\/.]\s*(\d{1,2})\s*-\s*(\d{1,2})\s*[\/.]\s*(\d{1,2})/);
    if (!match) return null;

    const diaAlta = Number.parseInt(match[1], 10);
    const mesAlta = Number.parseInt(match[2], 10);
    const diaBaja = Number.parseInt(match[3], 10);
    const mesBaja = Number.parseInt(match[4], 10);

    if (
        !Number.isFinite(diaAlta) || !Number.isFinite(diaBaja) ||
        !Number.isFinite(mesAlta) || !Number.isFinite(mesBaja) ||
        diaAlta < 1 || diaAlta > 31 || diaBaja < 1 || diaBaja > 31 ||
        mesAlta < 1 || mesAlta > 12 || mesBaja < 1 || mesBaja > 12
    ) {
        return null;
    }

    return { diaAlta, mesAlta, diaBaja, mesBaja };
};

const expandirMesesRegistroHasta0694 = (registro) => {
    const anio = normalizarAnioHasta0694(registro?.anio);
    const altaBaja = parsearAltaBajaHasta0694(registro?.altaBaja);
    if (!anio || !altaBaja) return [];

    const { mesAlta, mesBaja } = altaBaja;
    const anioBaja = mesBaja < mesAlta ? anio + 1 : anio;
    const desdeYm = `${anio}-${String(mesAlta).padStart(2, '0')}`;
    const hastaYm = `${anioBaja}-${String(mesBaja).padStart(2, '0')}`;

    return expandirRangoYearMonth(desdeYm, hastaYm).filter((ym) => ym <= '1994-06');
};

const tieneAltaBajaInformada = (registro) => String(registro?.altaBaja || '').trim().length > 0;

const expandirFallbackHastaPorCantidad = (registro, cantidad) => {
    if (!Number.isFinite(cantidad) || cantidad <= 0) return [];
    // Regla de negocio: cuando alta/baja no se puede usar como guía, el conteo lo manda
    // "meses trabajados". Se ancla al fin del año hacia atrás.
    const anioOriginal = normalizarAnioHasta0694(registro?.anio) || 1994;
    const anio = Math.min(anioOriginal, 1994);
    const mesAncla = anio >= 1994 ? 6 : 12;
    const meses = [];

    for (let i = 0; i < cantidad; i += 1) {
        const fecha = new Date(anio, mesAncla - 1 - i, 1);
        const ym = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        if (ym <= '1994-06') meses.push(ym);
    }

    return meses.sort((a, b) => a.localeCompare(b));
};

const aplicarCantidadSobrePeriodoHasta0694 = (mesesPeriodo, mesesTrabajados) => {
    if (!Array.isArray(mesesPeriodo) || mesesPeriodo.length === 0) return [];
    const cantidad = Number.parseInt(mesesTrabajados, 10);
    if (!Number.isFinite(cantidad) || cantidad <= 0) return [];

    // La cantidad efectiva se respeta, pero siempre dentro del período alta/baja.
    const tope = Math.min(cantidad, mesesPeriodo.length);
    return mesesPeriodo.slice(mesesPeriodo.length - tope);
};

/**
 * Analiza HASTA 06/94 en granularidad mensual real para evitar doble conteo.
 * La columna "mesesTrabajados" es el número autorizado; alta/baja+anio sólo se usa
 * para determinar CUÁLES meses específicos (mapeo a instancias de BD).
 * Si alta/baja no puede parsearse (aunque esté informada), se usa fallback por cantidad.
 *
 * @param {Array}  registros
 * @param {string|null} primerMesComputableYm - "YYYY-MM": excluye meses anteriores a los 18 años
 */
export const analizarRegistrosHasta0694 = (registros = [], primerMesComputableYm = null) => {
    const mesARegistros = new Map();
    let totalMesesDeclarados = 0;
    let registrosConPeriodoValido = 0;
    let registrosConFallback = 0;
    let mesesExcluidosPorEdad = 0;

    for (let index = 0; index < (registros || []).length; index += 1) {
        const r = registros[index];
        const sourceId = r?.indiceOriginal != null ? `idx:${r.indiceOriginal}` : `row:${index}`;
        const mesesTrabajados = Number.parseInt(r?.mesesTrabajados, 10);
        if (Number.isFinite(mesesTrabajados) && mesesTrabajados > 0) {
            totalMesesDeclarados += mesesTrabajados;
        }

        const mesesPeriodo = expandirMesesRegistroHasta0694(r);
        // Si el período no pudo derivarse de alta/baja (incluso si fue informada pero es inválida),
        // se usa el fallback por cantidad anclado al fin del año. Esto evita descartar meses silenciosamente.
        const mesesRegistro = mesesPeriodo.length > 0
            ? aplicarCantidadSobrePeriodoHasta0694(mesesPeriodo, mesesTrabajados)
            : expandirFallbackHastaPorCantidad(r, Number.isFinite(mesesTrabajados) ? mesesTrabajados : 0);

        if (mesesPeriodo.length > 0) {
            registrosConPeriodoValido += 1;
        } else if (mesesRegistro.length > 0) {
            registrosConFallback += 1;
        }

        for (const ym of mesesRegistro) {
            if (primerMesComputableYm && ym < primerMesComputableYm) {
                mesesExcluidosPorEdad += 1;
                continue;
            }
            if (!mesARegistros.has(ym)) mesARegistros.set(ym, new Set());
            mesARegistros.get(ym).add(sourceId);
        }
    }

    const mesesUnicos = Array.from(mesARegistros.keys()).sort((a, b) => a.localeCompare(b));
    const mesesSuperpuestos = Array.from(mesARegistros.values()).filter((set) => set.size > 1).length;

    return {
        totalMesesDeclarados,
        totalMesesUnicos: mesesUnicos.length,
        mesesUnicos,
        mesesSuperpuestos,
        registrosConPeriodoValido,
        registrosConFallback,
        registrosSinPeriodoValido: Math.max(0, (registros || []).length - registrosConPeriodoValido),
        mesesExcluidosPorEdad,
    };
};

const normalizarRazonSocialParaRegla = (razonSocial) => {
    return String(razonSocial || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
};

/**
 * Clasifica la razon social del Resumen en tipo de aporte y regla de computo.
 */
export const clasificarRazonSocialResumen = (razonSocial) => {
    const rs = normalizarRazonSocialParaRegla(razonSocial);

    if (rs.startsWith('PAGOS Y TRANSFERENCIAS MONOTRI')) {
        return {
            codigo: 'EXCLUIR_MONOTRIBUTO',
            tipoAporte: 'MONOTRIBUTO',
            excluirDelComputo: true,
            mensaje: 'Registro detectado como monotributo. Revisar en SICAM y cargar manualmente si corresponde.',
        };
    }

    if (
        rs.startsWith('PAGOS Y TRANSFERENCIAS AUTO') ||
        rs.startsWith('PAGOS Y TRANSF AUTONOMOS DESDE') ||
        rs.startsWith('PAGOS Y TRANSF AUTO')
    ) {
        return {
            codigo: 'EXCLUIR_AUTONOMO',
            tipoAporte: 'AUTONOMO',
            excluirDelComputo: true,
            mensaje: 'Registro detectado como autonomo. Revisar en SICAM y cargar manualmente si corresponde.',
        };
    }

    if (rs.startsWith('PAGOS Y TRANSF CASAS PARTICULA')) {
        return {
            codigo: 'ALERTA_SERVICIO_DOMESTICO',
            tipoAporte: 'SERVICIO_DOMESTICO',
            excluirDelComputo: false,
            mensaje: 'Periodo identificado como servicio domestico (casas particulares).',
        };
    }

    if (rs.startsWith('EFECTOR O MONOTRIBUTO SOCIAL')) {
        return {
            codigo: 'ALERTA_MONOTRIBUTO_SOCIAL',
            tipoAporte: 'MONOTRIBUTO_SOCIAL',
            excluirDelComputo: false,
            mensaje: 'Periodo identificado como monotributo social.',
        };
    }

    if (rs.startsWith('UCAP UNIDAD CANCELACION APORTE')) {
        return {
            codigo: 'ALERTA_UCAP_27705',
            tipoAporte: 'LEY_27705',
            excluirDelComputo: false,
            mensaje: 'Periodo identificado como UCAP (Ley 27705).',
        };
    }

    // E3: Subsidio/Prestación por Desempleo — aparece en Resumen pero nunca en HASTA 06/94.
    // Es computable, pero debe excluirse de la comparación pre-94 para no generar falsa discrepancia.
    if (
        rs.startsWith('SUBSIDIO POR DESEMPLEO') ||
        rs.startsWith('PRESTACION POR DESEMPLEO') ||
        rs.startsWith('FONDO NACIONAL DE EMPLEO') ||
        rs.startsWith('PRESTAC POR DESEMPLEO') ||
        rs.startsWith('SUBSIDIO DESEMPLEO')
    ) {
        return {
            codigo: 'ALERTA_SUBSIDIO_DESEMPLEO',
            tipoAporte: 'SUBSIDIO_DESEMPLEO',
            excluirDelComputo: false,
            mensaje: 'Periodo identificado como subsidio/prestación por desempleo.',
        };
    }

    return {
        codigo: 'RELACION_DEPENDENCIA',
        tipoAporte: 'RELACION_DEPENDENCIA',
        excluirDelComputo: false,
        mensaje: null,
    };
};

const calcularMesesUnicosRegistros = (registros) => {
    const set = new Set();
    for (const r of registros || []) {
        if (!r?.desde || !r?.hasta) continue;
        if (!validarPeriodo(r.desde) || !validarPeriodo(r.hasta) || !validarRango(r.desde, r.hasta)) continue;
        const meses = expandirRango(r.desde, r.hasta);
        for (const ym of meses) set.add(ym);
    }
    return set;
};

/**
 * Aplica reglas especiales del Resumen sin romper la logica de superposiciones.
 */
export const aplicarReglasEspecialesResumen = (registros = []) => {
    const registrosClasificados = (registros || []).map((r) => {
        const regla = clasificarRazonSocialResumen(r?.razonSocial);
        return {
            ...r,
            reglaResumen: regla.codigo,
            tipoAporteResumen: regla.tipoAporte,
            excluirDelComputo: regla.excluirDelComputo,
            mensajeReglaResumen: regla.mensaje,
        };
    });

    const registrosComputables = registrosClasificados.filter((r) => !r.excluirDelComputo);
    const registrosExcluidos = registrosClasificados.filter((r) => r.excluirDelComputo);

    const gruposAlerta = new Map();
    for (const reg of registrosClasificados) {
        if (reg.reglaResumen === 'RELACION_DEPENDENCIA') continue;

        const key = reg.reglaResumen;
        if (!gruposAlerta.has(key)) {
            gruposAlerta.set(key, {
                codigo: key,
                tipoAporte: reg.tipoAporteResumen,
                mensaje: reg.mensajeReglaResumen,
                registros: [],
            });
        }
        gruposAlerta.get(key).registros.push(reg);
    }

    const alertas = Array.from(gruposAlerta.values()).map((g) => {
        const mesesUnicos = calcularMesesUnicosRegistros(g.registros);
        return {
            ...g,
            cantidadRegistros: g.registros.length,
            cantidadMesesUnicos: mesesUnicos.size,
        };
    });

    return {
        registrosClasificados,
        registrosComputables,
        registrosExcluidos,
        alertas,
    };
};

/**
 * Calcula meses computables del Resumen aplicando descuentos de DESDE 07/94 por matcheo de período.
 * Evita doble descuento porque deduplica por yearMonth.
 */
export const calcularMesesComputablesResumenConDesde0794 = ({ mesesExpandidos = [], ajustesDesde0794 = [] }) => {
    const resumenSet = new Set(
        (mesesExpandidos || [])
            .map((m) => normalizarYearMonth(m?.yearMonth))
            .filter(Boolean)
    );

    const mesesPre94Set = new Set(Array.from(resumenSet).filter((ym) => ym < '1994-07'));
    const mesesPost94Set = new Set(Array.from(resumenSet).filter((ym) => ym >= '1994-07'));

    const periodosNoComputablesDesdeSet = new Set(
        (ajustesDesde0794 || [])
            .map((a) => normalizarYearMonth(a?.periodo))
            .filter((ym) => ym && ym >= '1994-07')
    );

    // Solo se descuenta si el período existe en el Resumen post-07/94.
    const periodosNoComputablesAplicadosSet = new Set(
        Array.from(periodosNoComputablesDesdeSet).filter((ym) => mesesPost94Set.has(ym))
    );

    const mesesPost94ComputablesSet = new Set(
        Array.from(mesesPost94Set).filter((ym) => !periodosNoComputablesAplicadosSet.has(ym))
    );

    const mesesComputablesResumen = [
        ...Array.from(mesesPre94Set),
        ...Array.from(mesesPost94ComputablesSet),
    ].sort((a, b) => a.localeCompare(b));

    return {
        mesesResumenUnicos: Array.from(resumenSet).sort((a, b) => a.localeCompare(b)),
        mesesPre94: Array.from(mesesPre94Set).sort((a, b) => a.localeCompare(b)),
        mesesPost94: Array.from(mesesPost94Set).sort((a, b) => a.localeCompare(b)),
        periodosNoComputablesDesde: Array.from(periodosNoComputablesDesdeSet).sort((a, b) => a.localeCompare(b)),
        periodosNoComputablesAplicados: Array.from(periodosNoComputablesAplicadosSet).sort((a, b) => a.localeCompare(b)),
        mesesPost94Computables: Array.from(mesesPost94ComputablesSet).sort((a, b) => a.localeCompare(b)),
        mesesComputablesResumen,
    };
};

/**
 * Expande un rango canonico YYYY-MM (inclusivo).
 */
export const expandirRangoYearMonth = (desdeYm, hastaYm) => {
    const desde = normalizarYearMonth(desdeYm);
    const hasta = normalizarYearMonth(hastaYm);
    if (!desde || !hasta) return [];
    if (desde > hasta) return [];

    const [startYear, startMonth] = desde.split('-').map((n) => parseInt(n, 10));
    const [endYear, endMonth] = hasta.split('-').map((n) => parseInt(n, 10));

    const meses = [];
    let year = startYear;
    let month = startMonth;
    while (year < endYear || (year === endYear && month <= endMonth)) {
        meses.push(`${year}-${String(month).padStart(2, '0')}`);
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return meses;
};

/**
 * Combina meses automáticos del Resumen (con descuentos DESDE 07/94) con meses manuales SICAM.
 * 
 * Retorna:
 * - mesesComputablesAutomaticos: Los meses que se pueden importar automáticamente
 * - mesesManualSicam: Los meses SICAM agregados manualmente (además de los automáticos)
 * - mesesComputablesDefinitivos: Union de ambos para import final
 * - totalMesesComputablesAutomaticos: Conteo de meses automáticos (sin SICAM)
 * - totalMesesComputablesDefinitivo: Conteo de union (automáticos + SICAM)
 */
export const calcularMesesDefinitivosConSicam = ({
    mesesExpandidos = [],
    ajustesDesde0794 = [],
    manualMesesSicam = [],
    mesesComputablesBase = [],
}) => {
    const baseNormalizada = (mesesComputablesBase || [])
        .map((m) => normalizarYearMonth(m?.yearMonth || m))
        .filter(Boolean);

    const automatico = baseNormalizada.length > 0
        ? {
            mesesComputablesResumen: Array.from(new Set(baseNormalizada)).sort((a, b) => a.localeCompare(b)),
        }
        : calcularMesesComputablesResumenConDesde0794({ mesesExpandidos, ajustesDesde0794 });

    const manualSet = new Set(
        (manualMesesSicam || [])
            .map((m) => normalizarYearMonth(m?.yearMonth || m))
            .filter(Boolean)
    );

    const finalSet = new Set([...automatico.mesesComputablesResumen, ...Array.from(manualSet)]);

    return {
        mesesComputablesAutomaticos: automatico.mesesComputablesResumen,
        mesesManualSicam: Array.from(manualSet).sort((a, b) => a.localeCompare(b)),
        mesesComputablesDefinitivos: Array.from(finalSet).sort((a, b) => a.localeCompare(b)),
        totalMesesComputablesAutomaticos: automatico.mesesComputablesResumen.length,
        totalMesesComputablesDefinitivo: finalSet.size,
        ...automatico,
    };
};

// ══════════════════════════════════════
// CRUCE INTEGRADO ENTRE SECCIONES
// ══════════════════════════════════════

/**
 * Cruza las secciones del PDF para generar correcciones y totales definitivos.
 * Replica la lógica del backend para poder recalcular al editar registros.
 *
 * @param {Object} params
 * @param {Array}  params.mesesExpandidos          - Meses expandidos del Resumen (registrosComputables)
 * @param {Array}  params.registrosHasta0694        - Registros de HASTA 06/94
 * @param {Array}  params.ajustesDesde0794          - Ajustes no computables de DESDE 07/94
 * @param {Array}  params.registrosClasificados     - Registros del Resumen con tipo clasificado (para E3)
 * @param {Array}  params.manualMesesSicamExpandidos - Meses SICAM manuales ya expandidos (para E3)
 * @param {string|null} params.primerMesComputableYm - Primer mes computable (18 años), "YYYY-MM"
 */
export const cruzarSecciones = ({
    mesesExpandidos,
    registrosHasta0694,
    ajustesDesde0794 = [],
    registrosClasificados = [],
    manualMesesSicamExpandidos = [],
    primerMesComputableYm = null,
}) => {
    const correcciones = [];

    const {
        mesesResumenUnicos,
        mesesPre94,
        mesesPost94,
        periodosNoComputablesAplicados,
        mesesPost94Computables,
    } = calcularMesesComputablesResumenConDesde0794({ mesesExpandidos, ajustesDesde0794 });

    // Aplicar filtro de 18 años a los meses pre-94 y post-94 del Resumen
    const mesesPre94Efectivos = primerMesComputableYm
        ? mesesPre94.filter(ym => ym >= primerMesComputableYm)
        : mesesPre94;
    const mesesPre94Count = mesesPre94Efectivos.length;
    const mesesPost94Efectivos = primerMesComputableYm
        ? mesesPost94.filter(ym => ym >= primerMesComputableYm)
        : mesesPost94;
    const mesesPost94Count = mesesPost94Efectivos.length;

    // E3: construir el conjunto de meses pre-94 a excluir de la comparación con HASTA 06/94.
    // UCAP (Ley 27705) y Subsidio por Desempleo aparecen en el Resumen pero nunca en HASTA 06/94,
    // por lo que no deben generar discrepancia. Lo mismo aplica a meses SICAM manuales pre-94.
    const TIPOS_EXCLUIR_DE_CRUCE = new Set(['LEY_27705', 'SUBSIDIO_DESEMPLEO']);
    const indicesExcluirDeCruce = new Set(
        (registrosClasificados || [])
            .filter(r => TIPOS_EXCLUIR_DE_CRUCE.has(r.tipoAporteResumen))
            .map(r => r.indiceOriginal)
    );
    const mesesResumenExcluirSet = new Set(
        (mesesExpandidos || [])
            .filter(m => m.origenIndices?.some(idx => indicesExcluirDeCruce.has(idx)))
            .map(m => normalizarYearMonth(m.yearMonth))
            .filter(Boolean)
    );
    const mesesSicamManualPre94Set = new Set(
        (manualMesesSicamExpandidos || [])
            .map(ym => normalizarYearMonth(ym?.yearMonth || ym))
            .filter(ym => ym && ym < '1994-07')
    );
    // Meses pre-94 del Resumen que DEBERÍAN estar en HASTA 06/94 (solo relación de dependencia)
    const mesesPre94ParaCruce = mesesPre94Efectivos.filter(
        ym => !mesesResumenExcluirSet.has(ym) && !mesesSicamManualPre94Set.has(ym)
    );
    const mesesPre94ParaCruceCount = mesesPre94ParaCruce.length;

    // ── Cruce PRE 07/94: Resumen vs HASTA 06/94 ──
    const analisisHasta = analizarRegistrosHasta0694(registrosHasta0694, primerMesComputableYm);
    const totalMesesHasta = analisisHasta.totalMesesUnicos;

    if (totalMesesHasta > 0 && mesesPre94ParaCruceCount > 0 && totalMesesHasta !== mesesPre94ParaCruceCount) {
        const diff = Math.abs(totalMesesHasta - mesesPre94ParaCruceCount);
        correcciones.push({
            fuente: 'HASTA_0694',
            severidad: diff > 6 ? 'CRITICO' : 'ADVERTENCIA',
            mensaje: `HASTA 06/94 aporta ${totalMesesHasta} meses únicos pre-07/94, pero el Resumen (dependencia) expande ${mesesPre94ParaCruceCount}. Diferencia: ${diff}`,
            mesesOriginales: mesesPre94ParaCruceCount,
            mesesCorregidos: totalMesesHasta,
            diferencia: mesesPre94ParaCruceCount - totalMesesHasta,
        });
    }

    // E2: si HASTA 06/94 está vacío pero el Resumen sí tiene pre-94, puede ser falla de parseo
    if (totalMesesHasta === 0 && mesesPre94Count > 0) {
        correcciones.push({
            fuente: 'HASTA_0694',
            severidad: 'ADVERTENCIA',
            mensaje: `${mesesPre94Count} mes${mesesPre94Count > 1 ? 'es' : ''} anterior${mesesPre94Count > 1 ? 'es' : ''} a 07/94 figura${mesesPre94Count > 1 ? 'n' : ''} en el Resumen pero NO en HASTA 06/94. Verificá esos períodos en el PDF: si son VÁLIDOS, dejalos (se cuentan como aportados) e idealmente cargalos en HASTA 06/94 para confirmarlos; si son DUDOSOS o inválidos, eliminalos del Resumen y van a quedar disponibles para comprar con la Ley 24.476 (lo más seguro).`,
            mesesOriginales: mesesPre94Count,
            mesesCorregidos: mesesPre94Count,
            diferencia: 0,
        });
    }

    if (analisisHasta.mesesSuperpuestos > 0) {
        correcciones.push({
            fuente: 'HASTA_0694',
            severidad: 'INFO',
            mensaje: `HASTA 06/94 tiene ${analisisHasta.mesesSuperpuestos} mes${analisisHasta.mesesSuperpuestos > 1 ? 'es' : ''} superpuesto${analisisHasta.mesesSuperpuestos > 1 ? 's' : ''}; se computan una sola vez.`,
            mesesOriginales: analisisHasta.totalMesesDeclarados,
            mesesCorregidos: analisisHasta.totalMesesUnicos,
            diferencia: Math.max(0, analisisHasta.totalMesesDeclarados - analisisHasta.totalMesesUnicos),
        });
    }

    if (analisisHasta.mesesExcluidosPorEdad > 0) {
        correcciones.push({
            fuente: 'HASTA_0694',
            severidad: 'INFO',
            mensaje: `Se excluyeron ${analisisHasta.mesesExcluidosPorEdad} mes${analisisHasta.mesesExcluidosPorEdad > 1 ? 'es' : ''} de HASTA 06/94 por ser anteriores a los 18 años.`,
            mesesOriginales: analisisHasta.totalMesesUnicos + analisisHasta.mesesExcluidosPorEdad,
            mesesCorregidos: analisisHasta.totalMesesUnicos,
            diferencia: analisisHasta.mesesExcluidosPorEdad,
        });
    }

    // ── Cruce POST 07/94: Descuento por DESDE 07/94 ──
    // Solo contar descuentos que caigan dentro del período filtrado por 18 años
    const descuentosDesdeEnPeriodo = (periodosNoComputablesAplicados || []).filter(
        ym => !primerMesComputableYm || ym >= primerMesComputableYm
    );
    const descuentosDesdeAplicados = descuentosDesdeEnPeriodo.length;

    if (descuentosDesdeAplicados > 0) {
        correcciones.push({
            fuente: 'DESDE_0794',
            severidad: 'INFO',
            mensaje: `DESDE 07/94 detecta ${descuentosDesdeAplicados} mes${descuentosDesdeAplicados > 1 ? 'es' : ''} post-07/94 con REM IMP. SS en [0,10]. Se descuentan del cómputo final.`,
            mesesOriginales: mesesPost94Count,
            mesesCorregidos: Math.max(0, mesesPost94Count - descuentosDesdeAplicados),
            diferencia: descuentosDesdeAplicados,
        });
    }

    // ── Cálculo definitivo ──

    // Post-94: meses filtrados por edad + descontar no computables de DESDE 07/94
    const mesesPost94ComputablesFiltrados = primerMesComputableYm
        ? (mesesPost94Computables || []).filter(ym => ym >= primerMesComputableYm)
        : (mesesPost94Computables || []);
    const computablesPost94 = mesesPost94ComputablesFiltrados.length;

    // Pre-94 (fuente de dependencia): HASTA 06/94 si tiene registros, sino Resumen (E2 fix).
    const mesesDependenciaPre94 = totalMesesHasta > 0
        ? analisisHasta.mesesUnicos  // ya filtrados por primerMesComputableYm
        : (primerMesComputableYm
            ? mesesPre94.filter(ym => ym >= primerMesComputableYm)
            : [...mesesPre94]
        ).sort((a, b) => a.localeCompare(b));

    // E3: tipos especiales pre-94 (UCAP Ley 27705, Subsidio por Desempleo) y SICAM manual pre-94
    // Aparecen en el Resumen pero no en HASTA 06/94 → deben sumarse a los computables finales
    // independientemente de si HASTA 06/94 es la fuente primaria.
    const mesesEspecialesPre94 = Array.from(mesesResumenExcluirSet).filter(
        ym => ym < '1994-07' && (!primerMesComputableYm || ym >= primerMesComputableYm)
    );
    const mesesSicamManualPre94Computables = Array.from(mesesSicamManualPre94Set).filter(
        ym => !primerMesComputableYm || ym >= primerMesComputableYm
    );

    // mesesComputablesBase y totalMesesComputables: SIN SICAM manual.
    // El contador grande del paso 2 muestra solo aportes base (dependencia + tipos especiales).
    // El SICAM se suma por separado en la fórmula visual y en calculoDefinitivoConSicam.
    // Incluir SICAM aquí causaría doble conteo: aparecería en el contador grande Y en el chip SICAM.
    const mesesComputablesBaseSinSicam = new Set([
        ...mesesDependenciaPre94,
        ...mesesEspecialesPre94,
        ...mesesPost94ComputablesFiltrados,
    ]);
    const mesesComputablesBase = Array.from(mesesComputablesBaseSinSicam).sort((a, b) => a.localeCompare(b));

    const totalMesesComputables = mesesComputablesBaseSinSicam.size;
    const totalMesesNoComputables = Math.max(0, mesesResumenUnicos.length - totalMesesComputables);

    const mesesComputablesParaImportacion = mesesComputablesBase;

    return {
        correcciones,
        totalMesesComputables,
        totalMesesNoComputables,
        mesesComputablesBase,
        mesesComputablesParaImportacion,
    };
};

const toIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseIsoDate = (raw) => {
    const value = String(raw || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map((v) => Number(v));
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return null;
    }
    return date;
};

const sumarAnios = (date, years) => {
    const d = new Date(date.getTime());
    d.setFullYear(d.getFullYear() + years);
    return d;
};

// Suma una cantidad de años que puede ser fraccionaria (ej. 57,5 => 57 años + 6 meses).
// Necesario para la edad requerida prorrateada por regímenes diferenciales.
const sumarAniosDecimal = (date, aniosDecimal) => {
    const total = Number(aniosDecimal) || 0;
    const anios = Math.floor(total);
    const meses = Math.round((total - anios) * 12);
    const d = new Date(date.getTime());
    d.setFullYear(d.getFullYear() + anios);
    d.setMonth(d.getMonth() + meses);
    return d;
};

const primerDiaMesSiguiente = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);

const inicioMes = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const toYearMonthFromDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const calcularEdadEnAnios = (fechaNacimiento, fechaReferencia) => {
    let edad = fechaReferencia.getFullYear() - fechaNacimiento.getFullYear();
    const mesCumple = fechaNacimiento.getMonth();
    const diaCumple = fechaNacimiento.getDate();
    if (
        fechaReferencia.getMonth() < mesCumple ||
        (fechaReferencia.getMonth() === mesCumple && fechaReferencia.getDate() < diaCumple)
    ) {
        edad -= 1;
    }
    return edad;
};

const filtrarMesesLibres = (meses, mesesComputablesSet) => meses.filter((ym) => !mesesComputablesSet.has(ym));

/**
 * Calcula opciones informativas para completar aportes hacia 360 meses.
 * No modifica la lógica de importación: solo estima potenciales meses libres.
 *
 * @param {string[]} mesesHasta0694 - Meses únicos de HASTA 06/94 (para E4: calcular ventana 24.476 correctamente)
 * @param {string[]} manualMesesSicamLey24476 - Meses SICAM manuales de tipo LEY_24476 (para M2, fase 2)
 */
/**
 * Aplica un override manual de ventana (desde/hasta) sobre los valores por defecto.
 * Solo cambia los límites; el conteo de meses "libres" se sigue calculando aparte, así que
 * los meses ya aportados dentro de la ventana igual se descuentan.
 * Si el override es inválido (fechas mal formadas o desde > hasta), se usa el default.
 */
const aplicarOverrideVentanaYm = (defaultInicioYm, defaultFinYm, override) => {
    const iniOv = normalizarYearMonth(override?.inicioYm);
    const finOv = normalizarYearMonth(override?.finYm);
    const inicioYm = iniOv || defaultInicioYm;
    const finYm = finOv || defaultFinYm;
    if (inicioYm && finYm && inicioYm <= finYm) return { inicioYm, finYm };
    return { inicioYm: defaultInicioYm, finYm: defaultFinYm };
};

export const calcularOpcionesComplementoAportes = ({
    fechaNacimiento,
    sexo,
    mesesComputablesDefinitivos = [],
    hijos = [],
    totalComputablesOverride = null,
    estrategiasSeleccionadas = {},
    fechaActual = new Date(),
    mesesHasta0694 = [],
    manualMesesSicamLey24476 = [],
    // SICAM manual "No aportante": meses que la persona NO aportó pero que ya no se pueden comprar
    // (ocupan lugar). Bloquean las ventanas de moratorias y estrategias; no suman como computables.
    mesesNoAportante = [],
    ventanasOverride = {},
    // Prorrateo por regímenes diferenciales (opcional): si no se pasan, se usa el requisito
    // general (360 meses y edad 65/60 según sexo) => comportamiento clásico sin cambios.
    serviciosRequeridosMeses = 360,
    edadRequeridaAnios = null,
    // Jubilación provincial de Córdoba (otorgante Córdoba): no se computan hijos ni la compra
    // de aportes (moratorias). Solo se pueden comprar meses en huecos para cambiar la otorgante.
    esJubilacionProvincial = false,
}) => {
    const fechaNac = parseIsoDate(fechaNacimiento);
    const sexoNorm = String(sexo || '').trim().toUpperCase();

    if (!fechaNac) {
        return {
            valido: false,
            motivo: 'FECHA_NACIMIENTO_INVALIDA',
        };
    }
    if (sexoNorm !== 'M' && sexoNorm !== 'F') {
        return {
            valido: false,
            motivo: 'SEXO_INVALIDO',
        };
    }

    const edadJubilatoriaGeneral = sexoNorm === 'F' ? 60 : 65;
    // Edad requerida: la prorrateada por regímenes diferenciales si aplica, si no la general.
    const edadJubilatoria = (Number.isFinite(edadRequeridaAnios) && edadRequeridaAnios > 0)
        ? edadRequeridaAnios
        : edadJubilatoriaGeneral;
    const edadMinLey27705 = edadJubilatoria - 10;
    // Requisito de servicios (meta de meses): prorrateado si aplica, si no 360 (30 años).
    const metaMeses = Math.max(0, Math.round(Number(serviciosRequeridosMeses) || 360));

    // E5: usar fechaActual completa (con día) para cálculo preciso de edad.
    // La función calcularEdadEnAnios ya compara día/mes/año correctamente.
    const fechaRef = fechaActual instanceof Date ? fechaActual : new Date(fechaActual);
    const mesesComputablesSet = new Set(
        (mesesComputablesDefinitivos || [])
            .map((m) => normalizarYearMonth(m?.yearMonth || m))
            .filter(Boolean)
    );

    // Meses que no se pueden comprar: los computables y los "No aportante". Solo se usa para
    // descontar de las ventanas; los totales siguen saliendo de mesesComputablesSet.
    const mesesOcupadosSet = new Set(mesesComputablesSet);
    for (const ym of mesesNoAportante || []) {
        const n = normalizarYearMonth(ym?.yearMonth || ym);
        if (n) mesesOcupadosSet.add(n);
    }

    const fechaCumple18 = sumarAnios(fechaNac, 18);
    const fechaJubilacion = sumarAniosDecimal(fechaNac, edadJubilatoria);
    const edadActual = calcularEdadEnAnios(fechaNac, fechaRef);

    const v24476 = aplicarOverrideVentanaYm(
        toYearMonthFromDate(primerDiaMesSiguiente(fechaCumple18)),
        '1993-09',
        ventanasOverride?.ley24476,
    );
    const inicio24476Ym = v24476.inicioYm;
    const fin24476Ym = v24476.finYm;
    const meses24476Rango = inicio24476Ym <= fin24476Ym
        ? expandirRangoYearMonth(inicio24476Ym, fin24476Ym)
        : [];

    // Meses libres de la ventana 24.476 = ventana − meses ya aportados/computables que caen
    // dentro de ella. Se descuenta:
    //  - cualquier mes ya computable en la ventana (misma lógica que monotributo/27705),
    //  - los meses de HASTA 06/94 (robustez extra por si el set de computables llega vacío o incompleto),
    //  - los SICAM de tipo Ley 24.476 ya comprados.
    // TODO v2: identificar los huecos específicos en la ventana para sugerir qué meses comprar.
    const hasta0694VentanaSet = new Set(
        (mesesHasta0694 || []).map(ym => normalizarYearMonth(ym)).filter(Boolean)
    );
    const ley24476SicamSet = new Set(
        (manualMesesSicamLey24476 || []).map(ym => normalizarYearMonth(ym)).filter(Boolean)
    );
    const meses24476Libres = meses24476Rango.filter(
        ym => !mesesOcupadosSet.has(ym)
            && !hasta0694VentanaSet.has(ym)
            && !ley24476SicamSet.has(ym)
    );

    // Monotributo futuro: disponible solo si la persona NO alcanzó aún la edad jubilatoria.
    // Se compara por fecha exacta: desde el día del cumpleaños (inclusive) queda bloqueado.
    const monotributoHabilitado = fechaRef < fechaJubilacion;
    const vMono = aplicarOverrideVentanaYm(
        toYearMonthFromDate(fechaRef),
        toYearMonthFromDate(inicioMes(fechaJubilacion)),
        ventanasOverride?.monotributoFuturo,
    );
    const inicioMonoYm = vMono.inicioYm;
    const finMonoYm = vMono.finYm;
    const mesesMonotributoRango = monotributoHabilitado && inicioMonoYm <= finMonoYm
        ? expandirRangoYearMonth(inicioMonoYm, finMonoYm)
        : [];
    const mesesMonotributoLibres = filtrarMesesLibres(mesesMonotributoRango, mesesOcupadosSet);

    // Ley 27705: habilitada desde los 55 (hombres) / 50 (mujeres) y bloqueada desde el día
    // del cumpleaños de la edad jubilatoria (usar < para que el día exacto ya quede fuera).
    const fechaCumpleEdadMin27705 = sumarAniosDecimal(fechaNac, edadMinLey27705);
    const elegibleLey27705 = fechaRef >= fechaCumpleEdadMin27705 && fechaRef < fechaJubilacion;
    const v27705 = aplicarOverrideVentanaYm('1993-10', '2012-03', ventanasOverride?.ley27705);
    const inicio27705Ym = v27705.inicioYm;
    const fin27705Ym = v27705.finYm;
    const meses27705Rango = elegibleLey27705
        ? expandirRangoYearMonth(inicio27705Ym, fin27705Ym)
        : [];
    const meses27705Libres = filtrarMesesLibres(meses27705Rango, mesesOcupadosSet);

    const totalComputablesDetectados = mesesComputablesSet.size;
    const totalComputablesActual = Number.isFinite(totalComputablesOverride)
        ? Math.max(0, Math.floor(totalComputablesOverride))
        : totalComputablesDetectados;
    const hijosNormalizados = sexoNorm === 'F'
        ? (hijos || []).map((hijo, index) => {
            const auh = Boolean(hijo?.auh);
            const discapacidad = Boolean(hijo?.discapacidad);
            const adoptado = Boolean(hijo?.adoptado);
            const aniosBonificados = 1 + (auh ? 2 : 0) + (discapacidad ? 1 : 0) + (adoptado ? 1 : 0);
            return {
                indice: index + 1,
                auh,
                discapacidad,
                adoptado,
                aniosBonificados,
                mesesBonificados: aniosBonificados * 12,
            };
        })
        : [];

    // En jubilación provincial (Córdoba) no se computa la bonificación por hijos.
    const mesesBonificadosHijos = esJubilacionProvincial
        ? 0
        : hijosNormalizados.reduce((acc, hijo) => acc + hijo.mesesBonificados, 0);

    // M1: Bonificación por exceso de edad.
    // Por cada 2 meses completos que la persona superó la edad jubilatoria, se suma 1 mes computable.
    const mesesExcedidosJubilacion = fechaRef >= fechaJubilacion
        ? (fechaRef.getFullYear() * 12 + fechaRef.getMonth()) - (fechaJubilacion.getFullYear() * 12 + fechaJubilacion.getMonth())
        : 0;
    const bonificacionExcesoEdadMeses = Math.floor(mesesExcedidosJubilacion / 2);

    const totalComputablesConBonificacion = totalComputablesActual + mesesBonificadosHijos + bonificacionExcesoEdadMeses;
    const faltantesA360 = Math.max(0, metaMeses - totalComputablesConBonificacion);

    // En jubilación provincial (Córdoba) no se puede comprar aportes (moratorias) => máximos en 0.
    const estrategiaMaximos = esJubilacionProvincial
        ? { ley24476: 0, monotributoFuturo: 0, ley27705: 0 }
        : {
            ley24476: meses24476Libres.length,
            monotributoFuturo: mesesMonotributoLibres.length,
            ley27705: elegibleLey27705 ? meses27705Libres.length : 0,
        };

    const normalizarSeleccion = (valor, maximo) => {
        const num = Number.isFinite(Number(valor)) ? Math.floor(Number(valor)) : 0;
        return Math.max(0, Math.min(num, maximo));
    };

    const estrategiaSeleccion = {
        ley24476: normalizarSeleccion(estrategiasSeleccionadas?.ley24476, estrategiaMaximos.ley24476),
        monotributoFuturo: normalizarSeleccion(estrategiasSeleccionadas?.monotributoFuturo, estrategiaMaximos.monotributoFuturo),
        ley27705: normalizarSeleccion(estrategiasSeleccionadas?.ley27705, estrategiaMaximos.ley27705),
    };

    const totalEstrategiasSeleccionadas =
        estrategiaSeleccion.ley24476 +
        estrategiaSeleccion.monotributoFuturo +
        estrategiaSeleccion.ley27705;
    const totalProyectadoConEstrategias = totalComputablesConBonificacion + totalEstrategiasSeleccionadas;
    // Resta desde faltantesA360 (entero puro) para evitar acumulación de errores al sumar/restar 360.
    const faltantesLuegoSeleccion = Math.max(0, faltantesA360 - totalEstrategiasSeleccionadas);
    const mesesCombinadosBase = meses24476Libres.length + mesesMonotributoLibres.length;
    const faltantesTrasBase = Math.max(0, faltantesA360 - mesesCombinadosBase);

    const requiereLey27705 = faltantesTrasBase > 0;
    const mesesCubiertosCon27705 = requiereLey27705 && elegibleLey27705
        ? Math.min(faltantesTrasBase, meses27705Libres.length)
        : 0;
    const mesesAComprarSugeridos27705 = mesesCubiertosCon27705;
    const faltantesFinales = Math.max(0, faltantesTrasBase - mesesCubiertosCon27705);

    const totalPotencial = meses24476Libres.length + mesesMonotributoLibres.length + meses27705Libres.length;

    // ── Aporte futuro como monotributista + exceso de edad (posterior a la edad jubilatoria) ──
    // Aplica cuando siguen faltando meses y:
    //   - la persona ya está excedida de edad (el monotributo común está deshabilitado, así que
    //     no hace falta seleccionar nada: es su única alternativa), o
    //   - se seleccionó la estrategia de monotributo futuro y aun así no alcanza.
    // Si con el monotributo se llega a 360, o no se seleccionó y todavía no está excedida, no aparece.
    // Pasada la edad jubilatoria, cada mes de tiempo suma 1 mes (aporte monotributo) + 0,5 mes
    // (exceso de edad, 1 cada 2 meses de exceso) = 1,5 computables por mes. Para cubrir N
    // faltantes → ceil(N * 2/3) meses.
    const faltantesFuturoMonotributo = faltantesLuegoSeleccion;
    const aplicaFuturoMonotributo = faltantesFuturoMonotributo > 0
        && (!monotributoHabilitado || estrategiaSeleccion.monotributoFuturo > 0);
    const mesesTiempoFuturoMonotributo = aplicaFuturoMonotributo
        ? Math.ceil((faltantesFuturoMonotributo * 2) / 3)
        : 0;
    // El régimen combinado corre desde la edad jubilatoria: si aún no la alcanzó, arranca ahí;
    // si ya está excedido, arranca hoy.
    const empiezaEnJubilacionFuturo = fechaRef < fechaJubilacion;
    const inicioFuturoMonotributo = empiezaEnJubilacionFuturo ? fechaJubilacion : fechaRef;
    const fechaProyectada360Date = new Date(inicioFuturoMonotributo.getTime());
    fechaProyectada360Date.setMonth(fechaProyectada360Date.getMonth() + mesesTiempoFuturoMonotributo);

    return {
        valido: true,
        fechaNacimiento: toIsoDate(fechaNac),
        sexo: sexoNorm,
        edadActual,
        edadJubilatoria,
        fechaCumple18: toIsoDate(fechaCumple18),
        fechaJubilacion: toIsoDate(fechaJubilacion),
        totalComputablesActual,
        totalComputablesDetectados,
        totalComputablesConBonificacion,
        esJubilacionProvincial,
        bonificacionHijos: {
            aplica: sexoNorm === 'F' && !esJubilacionProvincial,
            cantidadHijos: hijosNormalizados.length,
            mesesBonificados: mesesBonificadosHijos,
            aniosBonificados: mesesBonificadosHijos / 12,
            detalle: hijosNormalizados,
        },
        bonificacionExcesoEdad: {
            aplica: mesesExcedidosJubilacion > 0,
            mesesExcedidos: mesesExcedidosJubilacion,
            mesesBonificados: bonificacionExcesoEdadMeses,
        },
        faltantesA360,
        metaServiciosMeses: metaMeses,
        estrategiaBase: {
            mesesLey24476: meses24476Libres.length,
            mesesMonotributoFuturo: mesesMonotributoLibres.length,
            mesesCombinados: mesesCombinadosBase,
            alcanza360: faltantesTrasBase === 0,
            faltantesTrasBase,
        },
        estrategias: {
            maximos: estrategiaMaximos,
            seleccion: estrategiaSeleccion,
            totalSeleccionado: totalEstrategiasSeleccionadas,
            totalProyectadoConEstrategias,
            faltantesLuegoSeleccion,
        },
        fallbackLey27705: {
            requerido: requiereLey27705,
            elegible: elegibleLey27705,
            mesesDisponibles: meses27705Libres.length,
            mesesAComprarSugeridos: mesesAComprarSugeridos27705,
            faltantesFinales,
        },
        aporteFuturoMonotributo: {
            aplica: aplicaFuturoMonotributo,
            mesesAportesFaltantes: faltantesFuturoMonotributo,
            mesesTiempoNecesario: mesesTiempoFuturoMonotributo,
            empiezaEnJubilacion: empiezaEnJubilacionFuturo,
            inicioYm: toYearMonthFromDate(inicioFuturoMonotributo),
            fechaProyectada360: toIsoDate(fechaProyectada360Date),
            // Mes siguiente a completar los aportes (inicio + T meses ya representa ese mes).
            fechaEstimadaYm: toYearMonthFromDate(fechaProyectada360Date),
        },
        totalPotencial,
        faltantesLuegoDePotenciales: Math.max(0, faltantesA360 - totalPotencial),
        ley24476: {
            inicioYm: inicio24476Ym,
            finYm: fin24476Ym,
            mesesRango: meses24476Rango.length,
            mesesLibres: meses24476Libres.length,
        },
        monotributoFuturo: {
            inicioYm: inicioMonoYm,
            finYm: finMonoYm,
            mesesRango: mesesMonotributoRango.length,
            mesesLibres: mesesMonotributoLibres.length,
        },
        ley27705: {
            elegible: elegibleLey27705,
            edadMinima: edadMinLey27705,
            edadMaxima: edadJubilatoria,
            inicioYm: inicio27705Ym,
            finYm: fin27705Ym,
            mesesRango: meses27705Rango.length,
            mesesLibres: meses27705Libres.length,
        },
    };
};

// Formatea una cantidad de meses (entera) como "X años Y meses".
const formatearMesesAniosMeses = (totalMeses) => {
    const m = Math.max(0, Math.round(totalMeses));
    const a = Math.floor(m / 12);
    const mm = m % 12;
    return mm === 0 ? `${a} años` : `${a} años ${mm} meses`;
};

/**
 * Prorrateo previsional ANSES para combinar regímenes con distintos requisitos.
 *
 * Cada régimen es un par (edad mínima E, servicios mínimos S en meses). El general es 65/30 (H)
 * o 60/30 (M). Se ancla en el régimen MENOS favorable (mayor S; a igual S, mayor E) y se le resta
 * la reducción prorrateada del servicio de cada régimen más favorable (fórmula del documento ANSES):
 *
 *   serviciosReq = S_base − Σ_{i≠base} min(mesesᵢ, Sᵢ) · (S_base − Sᵢ) / Sᵢ
 *   edadReq      = E_base − Σ_{i≠base} min(mesesᵢ, Sᵢ) · (E_base − Eᵢ) / Sᵢ
 *   cumple servicios ⟺ total de meses reales ≥ serviciosReq
 *
 * El servicio favorable se topea en Sᵢ (no se puede reducir por debajo del requisito del propio
 * régimen). Con piso en el régimen más favorable presente. Ejemplo del documento: 12,5a@50/25 +
 * 15a@65/30 ⇒ 27,5 servicios / 57,5 edad.
 *
 * @param {Object}  p
 * @param {'M'|'F'} p.sexo
 * @param {number}  p.mesesGenerales     Meses del régimen general (ANSES normal + comprados + sin etiquetar).
 * @param {Array}   p.regimenesConMeses  [{ nombre, edadMinima, serviciosMinimosMeses, meses }] por régimen diferencial.
 */
export const calcularProrrateoRegimenes = ({ sexo, mesesGenerales = 0, regimenesConMeses = [] }) => {
    const sexoNorm = String(sexo || '').trim().toUpperCase();
    const edadGeneral = sexoNorm === 'F' ? 60 : 65;
    const SERVICIOS_GENERAL_MESES = 360;

    const diferenciales = (regimenesConMeses || [])
        .filter((r) => r && Number(r.meses) > 0 && Number(r.serviciosMinimosMeses) > 0 && Number(r.edadMinima) > 0)
        .map((r) => ({
            nombre: r.nombre,
            E: Number(r.edadMinima),
            S: Number(r.serviciosMinimosMeses),
            meses: Number(r.meses),
            esGeneral: false,
        }));

    const mesesGen = Math.max(0, Number(mesesGenerales) || 0);

    // Sin regímenes diferenciales => requisito general clásico (comportamiento actual).
    if (diferenciales.length === 0) {
        return {
            aplica: false,
            edadGeneral,
            serviciosGeneralMeses: SERVICIOS_GENERAL_MESES,
            edadRequeridaAnios: edadGeneral,
            edadRequeridaMeses: edadGeneral * 12,
            edadRequeridaTexto: `${edadGeneral} años`,
            serviciosRequeridosMeses: SERVICIOS_GENERAL_MESES,
            cumpleServicios: mesesGen >= SERVICIOS_GENERAL_MESES,
            totalMesesComputados: mesesGen,
            componentes: [],
        };
    }

    const componentes = [...diferenciales];
    if (mesesGen > 0) {
        componentes.push({ nombre: 'Régimen general', E: edadGeneral, S: SERVICIOS_GENERAL_MESES, meses: mesesGen, esGeneral: true });
    }

    // Base = régimen menos favorable: mayor S; a igual S, mayor E.
    const base = componentes.reduce((a, b) => ((b.S > a.S) || (b.S === a.S && b.E > a.E) ? b : a));

    let serviciosReq = base.S;
    let edadReq = base.E;
    for (const c of componentes) {
        if (c === base) continue;
        const sUsado = Math.min(c.meses, c.S); // el servicio favorable se topea en su propio requisito
        serviciosReq -= sUsado * (base.S - c.S) / c.S;
        edadReq -= sUsado * (base.E - c.E) / c.S;
    }

    // Piso: no puede ser más favorable que el régimen más favorable presente.
    const minS = Math.min(...componentes.map((c) => c.S));
    const minE = Math.min(...componentes.map((c) => c.E));
    serviciosReq = Math.max(minS, serviciosReq);
    edadReq = Math.max(minE, edadReq);

    // Requisito => se redondea SIEMPRE hacia el mes siguiente (no se puede "cumplir a medias" un mes).
    const serviciosRequeridosMeses = Math.ceil(serviciosReq - 1e-9);
    const edadRequeridaMeses = Math.ceil(edadReq * 12 - 1e-9);
    const totalMesesComputados = componentes.reduce((acc, c) => acc + c.meses, 0);
    const cumpleServicios = totalMesesComputados >= serviciosRequeridosMeses;

    return {
        aplica: true,
        edadGeneral,
        serviciosGeneralMeses: SERVICIOS_GENERAL_MESES,
        edadRequeridaAnios: edadReq,
        edadRequeridaMeses,
        edadRequeridaTexto: formatearMesesAniosMeses(edadRequeridaMeses),
        serviciosRequeridosMeses,
        cumpleServicios,
        totalMesesComputados,
        componentes: componentes.map((c) => ({
            nombre: c.nombre,
            edadMinima: c.E,
            serviciosMinimosMeses: c.S,
            meses: c.meses,
            esGeneral: !!c.esGeneral,
            esBase: c === base,
        })),
    };
};


/**
 * Cajas previsionales provinciales NO transferidas a ANSES (13 provincias con régimen propio).
 * Las 10 provincias + CABA transferidas cuentan como ANSES y no figuran acá.
 * Córdoba está marcada porque es la única jubilación provincial que inicia el estudio.
 */
export const CAJAS_PROVINCIALES = [
    { id: 'BUENOS_AIRES', nombre: 'Buenos Aires', instituto: 'Instituto de Previsión Social de la Provincia de Buenos Aires' },
    { id: 'CORDOBA', nombre: 'Córdoba', instituto: 'Caja de Jubilaciones, Pensiones y Retiros de Córdoba', esCordoba: true },
    { id: 'CORRIENTES', nombre: 'Corrientes', instituto: 'Instituto de Previsión Social de Corrientes' },
    { id: 'CHACO', nombre: 'Chaco', instituto: 'Instituto de Seguridad Social, Seguros y Préstamos de la provincia de Chaco' },
    { id: 'CHUBUT', nombre: 'Chubut', instituto: 'Instituto de Seguridad Social de Chubut' },
    { id: 'ENTRE_RIOS', nombre: 'Entre Ríos', instituto: 'Caja de Jubilaciones y Pensiones de Entre Ríos' },
    { id: 'FORMOSA', nombre: 'Formosa', instituto: 'Caja de Previsión de Formosa' },
    { id: 'LA_PAMPA', nombre: 'La Pampa', instituto: 'Instituto de Seguridad Social de La Pampa' },
    { id: 'MISIONES', nombre: 'Misiones', instituto: 'Instituto de Previsión Social Misiones' },
    { id: 'NEUQUEN', nombre: 'Neuquén', instituto: 'Instituto de Seguridad Social de la Provincia del Neuquén' },
    { id: 'SANTA_FE', nombre: 'Santa Fe', instituto: 'Caja de Jubilaciones y Pensiones de la Provincia de Santa Fe' },
    { id: 'SANTA_CRUZ', nombre: 'Santa Cruz', instituto: 'Caja de Previsión Social de la Provincia de Santa Cruz' },
    { id: 'TIERRA_DEL_FUEGO', nombre: 'Tierra del Fuego', instituto: 'Caja de Previsión Social de la Provincia de Tierra del Fuego' },
];

/**
 * Determina la caja otorgante = la que tiene más meses. Todos los meses suman al requisito
 * (eso ya lo maneja el prorrateo/cómputo general); acá solo se decide quién otorga.
 *
 * Reglas del estudio:
 *  - Otorgante ANSES => jubilación ANSES (las provinciales no transferidas se reconocen por
 *    reconocimiento de servicios). Es el default y gana los empates.
 *  - Otorgante Córdoba => jubilación PROVINCIAL de Córdoba (la que inicia el estudio) => alerta.
 *  - Otorgante otra provincial (más meses que ANSES) => notificar (provincial de otra provincia).
 *
 * @param {Object} p
 * @param {number} p.mesesGenerales  Meses ANSES (sin etiquetar + transferidas + comprados).
 * @param {Object} p.mesesPorCaja    { cajaId: cantidadMeses } para cajas provinciales no transferidas.
 */
export const calcularCajaOtorgante = ({ mesesGenerales = 0, mesesPorCaja = {} }) => {
    const gen = Math.max(0, Number(mesesGenerales) || 0);
    const provinciales = CAJAS_PROVINCIALES
        .map((c) => ({ id: c.id, nombre: c.nombre, esCordoba: !!c.esCordoba, meses: Math.max(0, Number(mesesPorCaja?.[c.id]) || 0) }))
        .filter((c) => c.meses > 0)
        .sort((a, b) => b.meses - a.meses);

    if (provinciales.length === 0) {
        return { aplica: false, otorgante: 'ANSES', tipo: 'ANSES', otorganteEsCordoba: false, mesesGenerales: gen, provinciales: [] };
    }

    const maxProv = provinciales[0];
    let otorgante = 'ANSES';
    let tipo = 'ANSES';
    if (maxProv.meses > gen) {
        otorgante = maxProv.nombre;
        tipo = maxProv.esCordoba ? 'CORDOBA_PROVINCIAL' : 'OTRA_PROVINCIAL';
    }

    return {
        aplica: true,
        otorgante,
        tipo,
        otorganteEsCordoba: tipo === 'CORDOBA_PROVINCIAL',
        mesesGenerales: gen,
        maxProvincial: maxProv,
        provinciales,
    };
};
